import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Alert, Badge, Modal } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase as FaUserTie, Check as FaCheck, X as FaTimes, Unlink as FaUnlink, Plus as FaPlus, Clock as FaClock, CircleCheck as FaCheckCircle, CircleX as FaTimesCircle, ChevronLeft as FaChevronLeft, ChevronRight as FaChevronRight } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';
import GradientButton from '../../components/GradientButton';

const VACATION_TYPES = {
  wypoczynkowy: { color: '#0d6efd', bg: '#e7f1ff', label: 'Wypoczynkowy' },
  okolicznościowy: { color: '#f59f00', bg: '#fff9db', label: 'Okolicznościowy' },
  bezpłatny: { color: '#e03131', bg: '#fff5f5', label: 'Bezpłatny' },
};

const REQUEST_STATUS = {
  pending: { label: 'Oczekuje', color: '#f59f00', bg: '#fff9db', icon: FaClock },
  approved: { label: 'Zaakceptowany', color: '#2b8a3e', bg: '#e8f5e9', icon: FaCheckCircle },
  rejected: { label: 'Odrzucony', color: '#e03131', bg: '#fff5f5', icon: FaTimesCircle },
  cancelled: { label: 'Anulowany', color: '#868e96', bg: '#f1f3f5', icon: FaTimesCircle },
};

const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];
const DAYS_PL = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];

function toDateStr(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDate(s) {
  return new Date(s).toLocaleDateString('pl-PL');
}

function countWorkingDays(start, end) {
  let count = 0;
  const cur = new Date(start);
  cur.setHours(12, 0, 0, 0);
  const endD = new Date(end);
  endD.setHours(12, 0, 0, 0);
  while (cur <= endD) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function MiniCalendar({ year, month, requests, selStart, selEnd, onDayClick, onDayHover }) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  const getRequest = (day) => {
    if (!day) return null;
    const ds = toDateStr(new Date(year, month, day));
    return requests.find(r => ['pending', 'approved'].includes(r.status) && ds >= r.start_date && ds <= r.end_date);
  };

  const inSel = (day) => {
    if (!day || !selStart) return false;
    const ds = toDateStr(new Date(year, month, day));
    const end = selEnd || selStart;
    const [a, b] = selStart <= end ? [selStart, end] : [end, selStart];
    return ds >= a && ds <= b;
  };

  const isWeekend = (day) => {
    if (!day) return false;
    const d = new Date(year, month, day).getDay();
    return d === 0 || d === 6;
  };

  const isToday = (day) => {
    if (!day) return false;
    const t = new Date();
    return day === t.getDate() && month === t.getMonth() && year === t.getFullYear();
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px', marginBottom: '4px' }}>
        {DAYS_PL.map(d => (
          <div key={d} style={{
            textAlign: 'center', fontSize: '11px', fontWeight: 600, padding: '4px 0',
            color: d === 'So' || d === 'Nd' ? '#e03131' : '#868e96',
          }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '3px' }}>
        {days.map((day, idx) => {
          const req = getRequest(day);
          const wknd = isWeekend(day);
          const sel = inSel(day) && !wknd && !req;
          const today = isToday(day);
          const typeMeta = req ? VACATION_TYPES[req.vacation_type] : null;

          let bg = 'transparent';
          let color = '#212529';
          let border = '1px solid transparent';

          if (!day) {
            color = 'transparent';
          } else if (wknd) {
            bg = '#f8f9fa';
            color = '#ced4da';
          } else if (req) {
            bg = req.status === 'pending' ? typeMeta.bg : typeMeta.color;
            color = req.status === 'pending' ? typeMeta.color : 'white';
            border = req.status === 'pending' ? `2px dashed ${typeMeta.color}` : '1px solid transparent';
          } else if (sel) {
            bg = '#4dabf7';
            color = 'white';
          } else if (today) {
            bg = '#e9ecef';
            color = '#212529';
          } else {
            bg = '#f8f9fa';
            color = '#212529';
          }

          const clickable = day && !wknd && !req;

          return (
            <div
              key={idx}
              onClick={() => clickable && onDayClick(toDateStr(new Date(year, month, day)))}
              onMouseEnter={() => clickable && onDayHover(toDateStr(new Date(year, month, day)))}
              style={{
                position: 'relative', aspectRatio: '1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '8px', fontSize: '13px', userSelect: 'none',
                cursor: clickable ? 'pointer' : 'default',
                fontWeight: req || sel ? 600 : 400,
                background: bg, color,
                border: today && !req && !sel ? '1px solid #dee2e6' : border,
              }}
            >
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}
function LinkRequests() {
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(true);

  const [activeLink, setActiveLink] = useState(null);
  const [loadingLink, setLoadingLink] = useState(true);

  const [vacationRequests, setVacationRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  const [selStart, setSelStart] = useState(null);
  const [selHover, setSelHover] = useState(null);

  const [pendingRange, setPendingRange] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [vacationType, setVacationType] = useState('wypoczynkowy');
  const [notes, setNotes] = useState('');

  const [showUnlinkModal, setShowUnlinkModal] = useState(false);

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const fetchInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const res = await api.get('/vacation/link-requests/');
      setInvites(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  const fetchActiveLink = useCallback(async () => {
    setLoadingLink(true);
    try {
      const res = await api.get('/vacation/linked-employer/');
      setActiveLink(res.data?.[0] || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLink(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
    fetchActiveLink();
  }, [fetchInvites, fetchActiveLink]);

  const fetchVacationRequests = useCallback(async (linkId) => {
    setLoadingRequests(true);
    try {
      const res = await api.get(`/vacation/links/${linkId}/vacation-requests/`);
      setVacationRequests(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    if (activeLink) {
      fetchVacationRequests(activeLink.id);
    } else {
      setVacationRequests([]);
    }
  }, [activeLink, fetchVacationRequests]);

  const prevMonth = () => { if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); } else setViewMonth(m => m + 1); };

  const handleDayClick = (ds) => {
    if (!selStart) {
      setSelStart(ds);
    } else {
      const [s, e] = selStart <= ds ? [selStart, ds] : [ds, selStart];
      setPendingRange({ start: s, end: e });
      setShowRequestModal(true);
      setSelStart(null);
      setSelHover(null);
      setError('');
    }
  };
 
  const handleRespondInvite = async (linkId, action) => {
      setError('');
      try {
        await api.post(`/vacation/link-requests/${linkId}/respond/`, { action });
        setSuccess(action === 'accept' ? 'Zaproszenie zaakceptowane' : 'Zaproszenie odrzucone');
        await fetchInvites();
        await fetchActiveLink();
        window.dispatchEvent(new Event('invitesUpdated')); // <-- dodaj to
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.response?.data?.detail || 'Nie udało się przetworzyć zaproszenia');
      }
    };

  const handleUnlinkClick = () => {
    setShowUnlinkModal(true);
  };

  const handleConfirmUnlink = async () => {
    if (!activeLink) return;
    setError('');
    try {
      await api.post(`/vacation/links/${activeLink.id}/unlink/`);
      setSuccess('Połączenie z pracodawcą zostało zakończone');
      setActiveLink(null);
      await fetchInvites();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się zakończyć połączenia');
    }
    setShowUnlinkModal(false);
  };

const handleSubmitRequest = async () => {
    if (!pendingRange || !activeLink) return;
    setError('');
    try {
      await api.post(`/vacation/links/${activeLink.id}/vacation-requests/`, {
        start_date: pendingRange.start,
        end_date: pendingRange.end,
        vacation_type: vacationType,
        notes,
      });
      setSuccess('Wniosek urlopowy został wysłany do pracodawcy');
      setShowRequestModal(false);
      setPendingRange(null);
      setNotes('');
      await fetchVacationRequests(activeLink.id);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(
        err.response?.data?.detail
        || err.response?.data?.non_field_errors?.[0]
        || 'Nie udało się wysłać wniosku'
      );
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!activeLink || !window.confirm('Wycofać ten wniosek urlopowy?')) return;
    setError('');
    try {
      await api.post(`/vacation/links/${activeLink.id}/vacation-requests/${requestId}/cancel/`);
      await fetchVacationRequests(activeLink.id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się wycofać wniosku');
    }
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">

        <motion.div {...fadeInUp} className="mb-4">
          <h1 className="pub-h2 mb-1">Połączenie z pracodawcą</h1>
          <p className="pub-muted mb-0">Zarządzaj zaproszeniami i składaj wnioski urlopowe</p>
        </motion.div>

        {success && <Alert variant="success" className="border-0" style={{ borderRadius: '15px' }}>{success}</Alert>}
        {error && <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>{error}</Alert>}

        {/* OCZEKUJĄCE ZAPROSZENIA */}
        {!loadingInvites && pendingInvites.length > 0 && (
          <motion.div {...fadeInUp} className="mb-4">
            <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <Card.Header style={{ padding: '1.25rem', borderLeft: '3px solid #f59f00' }}>
                <h6 className="mb-0 fw-bold">Oczekujące zaproszenia ({pendingInvites.length})</h6>
              </Card.Header>
              <Card.Body className="p-3">
                <AnimatePresence>
                  {pendingInvites.map(invite => (
                    <motion.div
                      key={invite.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="d-flex align-items-center justify-content-between p-3 mb-2 flex-wrap gap-2"
                      style={{ borderRadius: '14px', background: '#fff9db' }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FaUserTie color="#f59f00" />
                        <div>
                          <div style={{ color: '#212529', fontWeight: 600, fontSize: '14px' }}>
                            {invite.employer_company || invite.employer_name}
                          </div>
                          {/* <div style={{ color: '#868e96', fontSize: '12px' }}>{invite.employer_name}</div> */}
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-success d-flex align-items-center gap-1"
                          style={{ borderRadius: '10px' }}
                          onClick={() => handleRespondInvite(invite.id, 'accept')}
                        >
                          <FaCheck size={11} /> Akceptuj
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                          style={{ borderRadius: '10px' }}
                          onClick={() => handleRespondInvite(invite.id, 'reject')}
                        >
                          <FaTimes size={11} /> Odrzuć
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Card.Body>
            </Card>
          </motion.div>
        )}

        {loadingLink ? (
          <Loader />
        ) : !activeLink ? (
          <motion.div {...fadeInUp}>
            <Card
              className="border-0 shadow-lg text-center"
              style={{ padding: 0 }}
            >
              <Card.Body className="p-5">
                <FaUserTie size={40} strokeWidth={1.5} className="pub-small mb-3" />
                <h3 className="pub-h3 mb-3">Brak połączonego pracodawcy</h3>
                <p className="pub-muted mb-0">
                  Gdy pracodawca wyśle Ci zaproszenie, pojawi się ono powyżej — zaakceptuj je, żeby móc składać wnioski urlopowe.
                </p>
              </Card.Body>
            </Card>
          </motion.div>
        ) : (
          <motion.div {...fadeInUp}>
<Row className="g-4">

              {/* KALENDARZ + PULA */}
              <Col xs={12}>
                <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                  <Card.Header
                    className="d-flex align-items-center justify-content-between"
                    style={{ padding: '1.25rem' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FaUserTie color="var(--pub-navy)" />
                      <h6 className="mb-0 fw-bold">{activeLink.employer_company || activeLink.employer_name}</h6>
                    </div>
                    <button
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                      style={{ borderRadius: '10px' }}
                      onClick={handleUnlinkClick}
                    >
                      <FaUnlink size={11} /> Rozłącz
                    </button>
                  </Card.Header>
                  <Card.Body className="p-4">

                    {/* PULA URLOPOWA */}
                    <Row className="g-3 mb-4">
                      <Col xs={4}>
                        <div className="p-3 text-center" style={{ background: '#f1f3f5', borderRadius: '14px' }}>
                          <div className="fw-bold" style={{ fontSize: '1.8rem', color: '#212529' }}>
                            {activeLink.vacation_days_total}
                          </div>
                          <small style={{ color: '#868e96' }}>Przyznane</small>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="p-3 text-center" style={{ background: '#fff3e0', borderRadius: '14px' }}>
                          <div className="fw-bold" style={{ fontSize: '1.8rem', color: '#e67700' }}>
                            {activeLink.vacation_days_used}
                          </div>
                          <small style={{ color: '#868e96' }}>Użyte</small>
                        </div>
                      </Col>
                      <Col xs={4}>
                        <div className="p-3 text-center" style={{ background: '#e8f5e9', borderRadius: '14px' }}>
                          <div className="fw-bold" style={{ fontSize: '1.8rem', color: '#2b8a3e' }}>
                            {activeLink.vacation_days_available}
                          </div>
                          <small style={{ color: '#868e96' }}>Dostępne</small>
                        </div>
                      </Col>
                    </Row>

                    {/* NAWIGACJA MIESIĄCA */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <button onClick={prevMonth} className="btn btn-light" style={{ borderRadius: '10px' }}>
                        <FaChevronLeft />
                      </button>
                      <span className="fw-bold" style={{ fontSize: '1.1rem' }}>
                        {MONTHS_PL[viewMonth]} {viewYear}
                      </span>
                      <button onClick={nextMonth} className="btn btn-light" style={{ borderRadius: '10px' }}>
                        <FaChevronRight />
                      </button>
                    </div>

                    <MiniCalendar
                      year={viewYear} month={viewMonth} requests={vacationRequests}
                      selStart={selStart} selEnd={selStart ? (selHover || selStart) : null}
                      onDayClick={handleDayClick} onDayHover={ds => selStart && setSelHover(ds)}
                    />

                    {selStart && (
                      <Alert variant="info" className="border-0 mt-3" style={{ borderRadius: '12px' }}>
                        📅 Od: <strong>{formatDate(selStart)}</strong>
                        {selHover && selHover !== selStart && (
                          <>
                            {' '}→ <strong>{formatDate(selHover)}</strong>
                            {' '}— <strong>
                              {countWorkingDays(
                                ...(selStart <= selHover ? [selStart, selHover] : [selHover, selStart])
                              )} dni roboczych
                            </strong>
                          </>
                        )}
                        <br />
                        <small>Kliknij drugi dzień żeby wysłać wniosek — weekendy są pomijane</small>
                      </Alert>
                    )}

                    {/* LEGENDA */}
                    <div className="d-flex gap-3 flex-wrap mt-3">
                      {Object.entries(VACATION_TYPES).map(([key, val]) => (
                        <div key={key} className="d-flex align-items-center gap-1">
                          <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: val.color, display: 'inline-block' }} />
                          <small style={{ color: '#868e96' }}>{val.label}</small>
                        </div>
                      ))}
                      <div className="d-flex align-items-center gap-1">
                        <span style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px dashed #868e96', display: 'inline-block' }} />
                        <small style={{ color: '#868e96' }}>Oczekuje na akceptację</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* HISTORIA WNIOSKÓW */}
                <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                  <Card.Header style={{ background: '#f8f9fa', border: 'none', padding: '1.25rem' }}>
                    <h6 className="mb-0 fw-bold" style={{ color: '#212529' }}>Moje wnioski urlopowe</h6>
                  </Card.Header>
                  <Card.Body className="p-3">
                    {loadingRequests ? (
                      <Loader />
                    ) : vacationRequests.length === 0 ? (
                      <p className="text-center text-muted py-4 mb-0">Brak złożonych wniosków. Kliknij dzień w kalendarzu, żeby wysłać pierwszy.</p>
                    ) : (
                      <AnimatePresence>
                        {vacationRequests.map(r => {
                          const statusMeta = REQUEST_STATUS[r.status];
                          const StatusIcon = statusMeta.icon;
                          const typeMeta = VACATION_TYPES[r.vacation_type];
                          return (
                            <motion.div
                              key={r.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 10 }}
                              className="p-3 mb-2"
                              style={{
                                borderRadius: '14px',
                                background: typeMeta?.bg || '#f8f9fa',
                                border: `1px solid ${typeMeta?.color}33`,
                              }}
                            >
                              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: typeMeta?.color, display: 'inline-block' }} />
                                  <span style={{ color: '#212529', fontWeight: 600, fontSize: '14px' }}>
                                    {formatDate(r.start_date)}
                                    {r.start_date !== r.end_date && ` – ${formatDate(r.end_date)}`}
                                  </span>
                                  <Badge style={{ background: typeMeta?.color, fontSize: '11px' }}>{r.days_count} dni</Badge>
                                </div>
                                <span className="d-flex align-items-center gap-1">
                                  <StatusIcon color={statusMeta.color} size={12} />
                                  <small style={{ color: statusMeta.color, fontWeight: 600 }}>{statusMeta.label}</small>
                                  {r.status === 'pending' && (
                                    <button
                                      onClick={() => handleCancelRequest(r.id)}
                                      className="btn btn-outline-secondary btn-sm ms-2 py-0"
                                      style={{ borderRadius: '8px', fontSize: '12px' }}
                                    >
                                      Wycofaj
                                    </button>
                                  )}
                                </span>
                              </div>
                              {r.notes && <div className="text-muted small mt-2">{r.notes}</div>}
                              {['rejected', 'cancelled'].includes(r.status) && r.response_note && (
                                <div className="small mt-2" style={{ color: '#e03131' }}>
                                  Powód: {r.response_note}
                                </div>
                              )}
                            </motion.div>
                          );
                        })}
                      </AnimatePresence>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </motion.div>
        )}
        {/* MODAL WYSYŁANIA WNIOSKU URLOPOWEGO */}
        <Modal show={showRequestModal} onHide={() => { setShowRequestModal(false); setPendingRange(null); setError(''); }} centered>
          <Modal.Header closeButton style={{ border: 'none' }}>
            <Modal.Title>Nowy wniosek urlopowy</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {error && <Alert variant="danger" className="border-0" style={{ borderRadius: '12px' }}>{error}</Alert>}
            {pendingRange && (
              <Alert variant="light" className="border" style={{ borderRadius: '12px', color: '#212529' }}>
                <strong>{formatDate(pendingRange.start)}</strong>
                {pendingRange.start !== pendingRange.end && <> – <strong>{formatDate(pendingRange.end)}</strong></>}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label className="fw-medium">Rodzaj urlopu</Form.Label>
              <div className="d-flex gap-2">
                {Object.entries(VACATION_TYPES).map(([key, val]) => (
                  <div
                    key={key}
                    onClick={() => setVacationType(key)}
                    className="flex-fill p-2 text-center d-flex align-items-center justify-content-center gap-2"
                    style={{
                      borderRadius: '12px', cursor: 'pointer', fontSize: '0.85rem',
                      border: vacationType === key ? `2px solid ${val.color}` : '2px solid #e9ecef',
                      background: vacationType === key ? val.bg : 'transparent',
                      color: vacationType === key ? val.color : '#495057',
                      fontWeight: vacationType === key ? 700 : 400,
                    }}
                  >
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: val.color, display: 'inline-block', flexShrink: 0 }} />
                    {val.label}
                  </div>
                ))}
              </div>
            </Form.Group>

            {activeLink && activeLink.vacation_days_available <= 0 && vacationType === 'wypoczynkowy' && (
              <Alert variant="warning" className="border-0 mb-3" style={{ borderRadius: '10px', fontSize: '0.85rem' }}>
                Nie masz już dostępnych dni urlopu wypoczynkowego. Wybierz inny rodzaj urlopu lub skontaktuj się z pracodawcą.
              </Alert>
            )}

            <Form.Group className="mb-4">
              <Form.Label className="fw-medium">Notatki (opcjonalnie)</Form.Label>
              <Form.Control
                as="textarea" rows={2} value={notes}
                onChange={e => setNotes(e.target.value)} style={{ borderRadius: '12px' }}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button" className="btn btn-light"
                onClick={() => { setShowRequestModal(false); setPendingRange(null); setError(''); }}
                style={{ borderRadius: '12px' }}
              >
                Anuluj
              </button>
              <GradientButton gradient="blue" onClick={handleSubmitRequest}>
                <FaPlus className="me-2" /> Wyślij wniosek
              </GradientButton>
            </div>
          </Modal.Body>
        </Modal>
        {/* MODAL POTWIERDZENIA ROZŁĄCZENIA */}
        <Modal show={showUnlinkModal} onHide={() => setShowUnlinkModal(false)} centered>
          <Modal.Header closeButton style={{ border: 'none' }}>
            <Modal.Title>Potwierdzenie rozłączenia</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p>
              Czy na pewno chcesz zakończyć połączenie z pracodawcą{' '}
              <strong>{activeLink?.employer_company || activeLink?.employer_name}</strong>?
            </p>
            <p className="text-danger small fw-medium mb-0">
              Stracisz możliwość składania wniosków urlopowych do tego pracodawcy — będziesz mógł połączyć się ponownie w przyszłości, jeśli wyśle nowe zaproszenie.
            </p>
          </Modal.Body>
          <Modal.Footer style={{ border: 'none' }}>
            <button className="btn btn-light" onClick={() => setShowUnlinkModal(false)} style={{ borderRadius: '12px' }}>
              Anuluj
            </button>
            <button className="btn btn-danger" onClick={handleConfirmUnlink} style={{ borderRadius: '12px' }}>
              Rozłącz
            </button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

export default LinkRequests;